// AllUser.js
import React, { Component } from "react";
import axios from "axios";

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

class AllUser extends Component {
  constructor(props) {
    super(props);
    this.state = {
      users: [],
      filteredUsers: [],
      loading: true,
      error: null,
      search: "",
      deletingUserId: null,
      deletingSelected: false,
      resendingEmail: null,
      selectedUserIds: [],
    };
  }

  async componentDidMount() {
    this.fetchUsers();
  }

  fetchUsers = async () => {
    try {
      const res = await axios.get(`${EndPoint}/user/sumuser`, {
        withCredentials: true,
      });

      const users = Array.isArray(res.data) ? res.data : [];

      this.setState({
        users,
        filteredUsers: users,
        loading: false,
        error: null,
        selectedUserIds: [],
      });
    } catch (error) {
      console.error("Error fetching user summary:", error);
      this.setState({
        error: "Failed to load users",
        loading: false,
      });
    }
  };

  handleSearchChange = (e) => {
    const search = e.target.value;
    this.setState({ search }, this.filterUsers);
  };

  filterUsers = () => {
    const { users, search } = this.state;
    const term = search.trim().toLowerCase();

    if (!term) {
      this.setState({ filteredUsers: users });
      return;
    }

    const filteredUsers = users.filter((user) => {
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();

      return (
        String(user.id || "").toLowerCase().includes(term) ||
        String(user.firstName || "").toLowerCase().includes(term) ||
        String(user.lastName || "").toLowerCase().includes(term) ||
        String(user.email || "").toLowerCase().includes(term) ||
        fullName.includes(term)
      );
    });

    this.setState({ filteredUsers });
  };

  getSubscriptionText = (entry) => {
    if (!entry?.stripe) return "—";

    if (Array.isArray(entry.stripe)) {
      return (
        entry.stripe
          .map((item) => item?.subscriptionStatus)
          .filter(Boolean)
          .join(", ") || "—"
      );
    }

    return entry.stripe.subscriptionStatus || "—";
  };

  handleResendVerification = async (email) => {
    try {
      this.setState({ resendingEmail: email });

      await axios.post(
        `${EndPoint}/user/resend-verification`,
        { email },
        { withCredentials: true }
      );

      alert("Verification email sent!");
    } catch (error) {
      console.error("Error sending verification email:", error);
      alert("Failed to send verification email.");
    } finally {
      this.setState({ resendingEmail: null });
    }
  };

  handleDeleteUser = async (userId, email) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this user?\n\n${email}\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      this.setState({ deletingUserId: userId });

      await axios.delete(`${EndPoint}/user/${userId}`, {
        withCredentials: true,
      });

      const updatedUsers = this.state.users.filter((user) => user.id !== userId);
      const updatedSelectedUserIds = this.state.selectedUserIds.filter((id) => id !== userId);

      this.setState(
        {
          users: updatedUsers,
          deletingUserId: null,
          selectedUserIds: updatedSelectedUserIds,
        },
        this.filterUsers
      );

      alert("User deleted successfully.");
    } catch (error) {
      console.error("Error deleting user:", error);
      this.setState({ deletingUserId: null });
      alert("Failed to delete user.");
    }
  };

  handleToggleUser = (userId) => {
    this.setState((prevState) => {
      const exists = prevState.selectedUserIds.includes(userId);

      return {
        selectedUserIds: exists
          ? prevState.selectedUserIds.filter((id) => id !== userId)
          : [...prevState.selectedUserIds, userId],
      };
    });
  };

  handleToggleAllVisible = () => {
    this.setState((prevState) => {
      const visibleIds = prevState.filteredUsers.map((user) => user.id);
      const allVisibleSelected =
        visibleIds.length > 0 &&
        visibleIds.every((id) => prevState.selectedUserIds.includes(id));

      if (allVisibleSelected) {
        return {
          selectedUserIds: prevState.selectedUserIds.filter(
            (id) => !visibleIds.includes(id)
          ),
        };
      }

      const merged = [...new Set([...prevState.selectedUserIds, ...visibleIds])];
      return { selectedUserIds: merged };
    });
  };

  handleDeleteChecked = async () => {
    const { selectedUserIds, users } = this.state;

    if (selectedUserIds.length === 0) {
      alert("No users selected.");
      return;
    }

    const selectedUsers = users.filter((user) => selectedUserIds.includes(user.id));
    const emailPreview = selectedUsers
      .slice(0, 5)
      .map((user) => user.email)
      .join("\n");

    const extraCount =
      selectedUsers.length > 5 ? `\n...and ${selectedUsers.length - 5} more` : "";

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedUserIds.length} selected user(s)?\n\n${emailPreview}${extraCount}\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      this.setState({ deletingSelected: true });

      await Promise.all(
        selectedUserIds.map((userId) =>
          axios.delete(`${EndPoint}/user/${userId}`, {
            withCredentials: true,
          })
        )
      );

      const updatedUsers = users.filter((user) => !selectedUserIds.includes(user.id));

      this.setState(
        {
          users: updatedUsers,
          selectedUserIds: [],
          deletingSelected: false,
        },
        this.filterUsers
      );

      alert("Selected users deleted successfully.");
    } catch (error) {
      console.error("Error deleting selected users:", error);
      this.setState({ deletingSelected: false });
      alert("Failed to delete one or more selected users.");
    }
  };

  renderSummaryCards() {
    const { users } = this.state;

    const totalUsers = users.length;
    const verifiedUsers = users.filter((u) => u.isEmailVerified).length;
    const unverifiedUsers = totalUsers - verifiedUsers;

    return (
      <div className="grid grid-cols-3 md:grid-cols-3 gap-4 mb-6 xs:grid-cols-1 sm:grid-cols-1">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <p className="text-sm text-slate-500">Total Users</p>
          <p className="text-3xl font-bold text-slate-800 mt-2">{totalUsers}</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <p className="text-sm text-slate-500">Verified</p>
          <p className="text-3xl font-bold text-emerald-700 mt-2">{verifiedUsers}</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <p className="text-sm text-slate-500">Not Verified</p>
          <p className="text-3xl font-bold text-rose-600 mt-2">{unverifiedUsers}</p>
        </div>
      </div>
    );
  }

  render() {
    const {
      filteredUsers,
      loading,
      error,
      search,
      deletingUserId,
      deletingSelected,
      resendingEmail,
      selectedUserIds,
    } = this.state;

    const visibleIds = filteredUsers.map((user) => user.id);
    const allVisibleSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selectedUserIds.includes(id));
    const selectedCount = selectedUserIds.length;

    if (loading) {
      return (
        <div className="min-h-screen bg-slate-100 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              Loading...
            </div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="min-h-screen bg-slate-100 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-600 shadow-sm">
              {error}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-100 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-slate-900">All Users</h2>
            <p className="text-slate-600 mt-1">
              Manage users, verification status, and subscriptions.
            </p>
          </div>

          {this.renderSummaryCards()}

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 md:p-5 border-b border-slate-200 flex flex-col gap-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">User Directory</h3>
                  <p className="text-sm text-slate-500">
                    Search by id, name, or email
                  </p>
                </div>

                <input
                  type="text"
                  value={search}
                  onChange={this.handleSearchChange}
                  placeholder="Search users..."
                  className="w-full md:w-80 rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
                <div className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-800">{selectedCount}</span> user(s) selected
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={this.handleToggleAllVisible}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                  >
                    {allVisibleSelected ? "Uncheck Visible" : "Check Visible"}
                  </button>

                  <button
                    onClick={this.handleDeleteChecked}
                    disabled={selectedCount === 0 || deletingSelected}
                    className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                  >
                    {deletingSelected ? "Deleting Checked..." : "Delete Checked"}
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-600">
                    <th className="px-4 py-3 font-semibold w-12">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={this.handleToggleAllVisible}
                      />
                    </th>
                    <th className="px-4 py-3 font-semibold">User</th>
                    <th className="px-4 py-3 font-semibold">User ID</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold">Subscription</th>
                    <th className="px-4 py-3 font-semibold">Email Verified</th>
                    <th className="px-4 py-3 font-semibold text-center">Actions</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((entry) => {
                      const subscriptionText = this.getSubscriptionText(entry);
                      const isDeleting = deletingUserId === entry.id;
                      const isResending = resendingEmail === entry.email;
                      const isChecked = selectedUserIds.includes(entry.id);

                      return (
                        <tr
                          key={entry.id}
                          className={`transition-colors ${
                            isChecked ? "bg-rose-50" : "hover:bg-slate-50"
                          }`}
                        >
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => this.handleToggleUser(entry.id)}
                            />
                          </td>

                          <td className="px-4 py-4">
                            <div className="font-semibold text-slate-800">
                              {entry.firstName} {entry.lastName}
                            </div>
                          </td>

                          <td className="px-4 py-4 text-slate-500 break-all">
                            {entry.id}
                          </td>

                          <td className="px-4 py-4 text-slate-700 break-all">
                            {entry.email}
                          </td>

                          <td className="px-4 py-4">
                            <span className="inline-flex rounded-full bg-violet-100 text-violet-700 px-3 py-1 text-xs font-medium">
                              {subscriptionText}
                            </span>
                          </td>

                          <td className="px-4 py-4">
                            {entry.isEmailVerified ? (
                              <span className="inline-flex rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-semibold">
                                Verified
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full bg-amber-100 text-amber-700 px-3 py-1 text-xs font-semibold">
                                Not Verified
                              </span>
                            )}
                          </td>

                          <td className="px-4 py-4">
                            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-center gap-2">
                              {!entry.isEmailVerified && (
                                <button
                                  onClick={() => this.handleResendVerification(entry.email)}
                                  disabled={isResending}
                                  className="rounded-lg bg-sky-600 px-3 py-2 text-white font-medium hover:bg-sky-700 disabled:opacity-50"
                                >
                                  {isResending ? "Sending..." : "Resend Email"}
                                </button>
                              )}

                              <button
                                onClick={() => this.handleDeleteUser(entry.id, entry.email)}
                                disabled={isDeleting}
                                className="rounded-lg bg-rose-600 px-3 py-2 text-white font-medium hover:bg-rose-700 disabled:opacity-50"
                              >
                                {isDeleting ? "Deleting..." : "Delete User"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center p-8 text-slate-500">
                        No users found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default AllUser;